import { JSX, useMemo, useState } from "react";
import { useControl } from "../control/ControlContext";
import { MEASURES, type MeasureDef } from "../config/metrics";
import { useData } from "../data/DataContext";
import MetricChart from "./MetricChartsSection";
import { buildAxisFormatter } from "./chartFormatting";
import CumBarChart from "./charts/CumbarChart";
import DimensionLineChart from "./charts/DimensionLineChart";
import Tooltip from "./Tooltip";
import ChartPlaceholder from "./ChartPlaceholder";
import SearchableSelect from "./SearchableSelect";
import { DIMENSIONS } from "../config/dimensions";
import type { DimensionConfig, DimensionId } from "../config/dimensions";
import type { PreparedData, DimensionSeries, ChartData } from "../config/data";
import { PLOTS, FORCED_MEASURE_GRANULARITY, type SubchartToken, type TimeGrain } from "../config/plots";
import type { PlotConfig } from "../config/plots";
import type { Timeseries } from "../types";
import { resampleTimeseries, resampleContinuousData } from "../data/resample";
import { getMeasureTags, getMeasureOwner } from "../config/plotGroups";

const MEASURE_LOOKUP = new Map<string, MeasureDef>(MEASURES.map(measure => [measure.id, measure]));
const DIMENSION_MAP = new Map(DIMENSIONS.map(dimension => [dimension.id, dimension]));
const ALL_DIMENSION = DIMENSION_MAP.get("all");
const SINGLE_DIMENSIONS = DIMENSIONS.filter(dimension => dimension.id !== "all" && dimension.dim2Field === null);
const COMBO_DIMENSIONS = DIMENSIONS.filter(dimension => dimension.dim2Field !== null);

export default function Plots({
  selectedTag,
  selectedOwner,
  outlierOnly,
  outlierPlotIds,
}: {
  selectedTag: string | null;
  selectedOwner: string | null;
  outlierOnly?: boolean;
  outlierPlotIds?: Set<string>;
}): JSX.Element {
  const { preparedData, isLoading } = useData();
  const { timeRange, grainOverride } = useControl();

  const globalGrain = useMemo((): TimeGrain => {
    if (grainOverride) return grainOverride;
    if (!timeRange.start || !timeRange.end) return "day";
    const diffDays = (timeRange.end.getTime() - timeRange.start.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays > 63 ? "week" : "day";
  }, [grainOverride, timeRange]);

  const filteredPlots = useMemo(() => {
    return PLOTS.filter(plot => {
      if (selectedTag && !getMeasureTags(plot.measureId).includes(selectedTag)) return false;
      if (selectedOwner && getMeasureOwner(plot.measureId) !== selectedOwner) return false;
      if (outlierOnly && outlierPlotIds && !outlierPlotIds.has(plot.id)) return false;
      return true;
    });
  }, [selectedTag, selectedOwner, outlierOnly, outlierPlotIds]);

  if (!ALL_DIMENSION) return <div>Configuration error: 'all' dimension not found</div>;

  if (isLoading || !preparedData) {
    return (
      <div className="plots">
        {filteredPlots.map(plot => (
          <div key={plot.id} id={`chart-${plot.id}`}>
            <h3>{plot.label}</h3>
            <ChartPlaceholder />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="plots">
      {filteredPlots.map(plot => (
        <PlotSection key={plot.id} plot={plot} preparedData={preparedData} globalGrain={globalGrain} />
      ))}
    </div>
  );
}

interface DimensionFilter {
  dimensionId: DimensionId;
  value: string;
}

export function PlotSection({
  plot,
  preparedData,
  globalGrain,
}: {
  plot: PlotConfig;
  preparedData: PreparedData;
  globalGrain: TimeGrain;
}): JSX.Element | null {
  const chartId = `chart-${plot.id}`;

  const resolvedDimensions = useMemo(() => resolveSubchartDimensions(plot.subcharts?.dims), [plot.subcharts]);
  const measure = MEASURE_LOOKUP.get(plot.measureId);
  const [subchartsExpanded, setSubchartsExpanded] = useState(!plot.subcharts?.collapsed);
  const [dimensionFilter, setDimensionFilter] = useState<DimensionFilter | null>(null);

  const metricData = preparedData[plot.measureId];
  // For boxplot charts, get data from plot.id key; otherwise use "all"
  const dataKey = plot.chart === "boxplot" ? plot.id : ALL_DIMENSION?.id;
  const allData = dataKey ? metricData?.[dataKey] : undefined;

  // Get available dimensions for filtering (single dimensions only, excluding "all")
  // Respects filterDimensions config from measure if specified
  const filterableDimensions = useMemo(() => {
    if (!metricData || !measure) return [];
    return SINGLE_DIMENSIONS.filter(dim => {
      // Check if dimension is allowed by measure config
      if (measure.filterDimensions && !measure.filterDimensions.includes(dim.id)) {
        return false;
      }
      const dimData = metricData[dim.id] as DimensionSeries | undefined;
      return dimData && Object.keys(dimData).length > 0;
    });
  }, [metricData, measure]);

  // Get available values for the selected dimension (filter out values with no actual data)
  const filterDimensionValues = useMemo(() => {
    if (!dimensionFilter?.dimensionId || !metricData) return [];
    const dimData = metricData[dimensionFilter.dimensionId] as DimensionSeries | undefined;
    if (!dimData) return [];
    // Filter out dimension values where all timeseries data is null
    return Object.entries(dimData)
      .filter(([, series]) => series.data.some(v => v != null))
      .map(([key]) => key)
      .sort();
  }, [metricData, dimensionFilter?.dimensionId]);

  // Determine which data to show in main chart
  const mainChartData = useMemo((): ChartData | undefined => {
    // For stacked-bar and distribution-xmr, we need dimension data, not "all" data
    if (plot.chart === "stacked-bar" && plot.stackedBar) {
      return undefined; // Will use dimensionData prop instead
    }
    if (plot.chart === "distribution-xmr" && plot.distributionXmr) {
      return undefined; // Will use dimensionData prop instead
    }
    if (!allData) return undefined;
    if (!dimensionFilter || !metricData) return allData;
    const dimData = metricData[dimensionFilter.dimensionId] as DimensionSeries | undefined;
    if (!dimData || !dimData[dimensionFilter.value]) return allData;
    return dimData[dimensionFilter.value];
  }, [allData, metricData, dimensionFilter, plot.chart, plot.stackedBar, plot.distributionXmr]);

  // Get dimension data for stacked-bar charts
  const stackedBarDimensionData = useMemo((): DimensionSeries | undefined => {
    if (plot.chart !== "stacked-bar" || !plot.stackedBar) return undefined;
    return metricData?.[plot.stackedBar.dimension] as DimensionSeries | undefined;
  }, [plot.chart, plot.stackedBar, metricData]);

  // Get dimension data for distribution-xmr charts
  const distributionXmrDimensionData = useMemo((): DimensionSeries | undefined => {
    if (plot.chart !== "distribution-xmr" || !plot.distributionXmr) return undefined;
    return metricData?.[plot.distributionXmr.dimension] as DimensionSeries | undefined;
  }, [plot.chart, plot.distributionXmr, metricData]);

  // Discrete metrics are always stored as daily in DataProvider and resampled here based
  // on the resolved grain (which can be overridden by the Day/Week toggle).
  // Forced-daily metrics (unique users) use avg; cumulative metrics use sum; others use avg.
  const isForcedDaily = FORCED_MEASURE_GRANULARITY[plot.measureId] === "day";
  const resampleStrategy: "sum" | "average" = measure?.isCumulative && !isForcedDaily ? "sum" : "average";
  const resampleInPlots = globalGrain === "week" && plot.chart !== "boxplot";

  const displayMainChartData = useMemo((): ChartData | undefined => {
    if (!mainChartData || !resampleInPlots) return mainChartData;
    if (Array.isArray((mainChartData as Timeseries).timestamps)) {
      return resampleTimeseries(mainChartData as Timeseries, "week", resampleStrategy);
    }
    if ("mean" in mainChartData && "std" in mainChartData) {
      return resampleContinuousData(mainChartData, "week");
    }
    return mainChartData;
  }, [resampleInPlots, resampleStrategy, mainChartData]);

  const displayDimensionData = useMemo((): DimensionSeries | undefined => {
    const base = stackedBarDimensionData ?? distributionXmrDimensionData;
    if (!resampleInPlots || !base) return base;
    const result: DimensionSeries = {};
    for (const [key, series] of Object.entries(base)) {
      result[key] = resampleTimeseries(series, "week", resampleStrategy);
    }
    return result;
  }, [resampleInPlots, resampleStrategy, stackedBarDimensionData, distributionXmrDimensionData]);

  // Companion measures (PERF-1583): render main + companion measures as one
  // multi-line chart (e.g. the P5/P50/P95 shading-factor trio). Uses the "all"
  // dimension series of each measure, resampled like the main chart.
  const companionSeries = useMemo((): DimensionSeries | undefined => {
    if (!plot.companionMeasures || plot.companionMeasures.length === 0 || !ALL_DIMENSION) return undefined;
    const result: DimensionSeries = {};
    for (const companion of plot.companionMeasures) {
      // Respect the dimension filter: read the filtered slice when one is set,
      // otherwise the "all" series.
      let series: ChartData | undefined;
      if (dimensionFilter) {
        const dimData = preparedData[companion.measureId]?.[dimensionFilter.dimensionId] as DimensionSeries | undefined;
        series = dimData?.[dimensionFilter.value];
      } else {
        series = preparedData[companion.measureId]?.[ALL_DIMENSION.id];
      }
      if (!isTimeseriesChart(series)) continue;
      const strategy = plot.companionResample ?? resampleStrategy;
      result[companion.label] = resampleInPlots ? resampleTimeseries(series, "week", strategy) : series;
    }
    return Object.keys(result).length > 0 ? result : undefined;
  }, [
    plot.companionMeasures,
    plot.companionResample,
    dimensionFilter,
    preparedData,
    resampleInPlots,
    resampleStrategy,
  ]);

  // Build the title based on filter
  const mainChartTitle = useMemo(() => {
    const avgSuffix = isForcedDaily && globalGrain === "week" ? " (avg/day)" : "";
    if (plot.chart === "stacked-bar") return `${plot.label} Stacked`;
    if (plot.chart === "distribution-xmr") return plot.label; // Title is built in MetricChartsSection
    if (!dimensionFilter) return `${plot.label}${avgSuffix}`;
    const dim = DIMENSION_MAP.get(dimensionFilter.dimensionId);
    return `${plot.label} (${dim?.label}: ${dimensionFilter.value})${avgSuffix}`;
  }, [plot.label, dimensionFilter, plot.chart, isForcedDaily, globalGrain]);

  // Build options for dimension and value selects
  const dimensionOptions = useMemo(() => {
    return [{ value: "", label: "All" }, ...filterableDimensions.map(dim => ({ value: dim.id, label: dim.label }))];
  }, [filterableDimensions]);

  const valueOptions = useMemo(() => {
    return filterDimensionValues.map(v => ({ value: v, label: v }));
  }, [filterDimensionValues]);

  // Early returns after all hooks
  if (!measure) {
    console.warn(`No measure config found for plot ${plot.measureId}`);
    return null;
  }

  if ((plot.chart === "xmr" || plot.chart === "xmr-log") && measure.kind !== "discrete") return null;
  if (plot.chart === "x" && measure.kind !== "continuous") return null;

  // For stacked-bar and distribution-xmr charts, require dimension data; for others, require mainChartData
  if (plot.chart === "stacked-bar") {
    if (!metricData || !stackedBarDimensionData) {
      return null;
    }
  } else if (plot.chart === "distribution-xmr") {
    if (!metricData || !distributionXmrDimensionData) {
      return null;
    }
  } else if (!metricData || !allData || !mainChartData) {
    return null;
  }

  const axisFormatting = buildAxisFormatter(measure);
  const axisFormatter = axisFormatting.axisFormatter;
  const axisName = axisFormatting.axisName;
  // Determine subchart type: explicit config > ratio/continuous default > bar
  const subchartType = plot.subcharts?.chartType;
  const useLineCharts =
    subchartType === "line" || (subchartType !== "bar" && (measure.kind === "continuous" || isRatioMeasure(measure)));
  const dimensionCharts: JSX.Element[] = [];
  const ratioSeries =
    plot.ratioComponents && ALL_DIMENSION ? getRatioSeries(plot, preparedData, ALL_DIMENSION.id) : undefined;

  // Boxplot, stacked-bar, and distribution-xmr charts already aggregate across a dimension, so skip dimension subcharts
  const skipDimensionCharts =
    plot.chart === "boxplot" || plot.chart === "stacked-bar" || plot.chart === "distribution-xmr";

  for (const dimension of skipDimensionCharts ? [] : resolvedDimensions) {
    let dimensionSeries = metricData[dimension.id] as DimensionSeries | undefined;
    if (!dimensionSeries || Object.keys(dimensionSeries).length === 0) continue;

    // Apply top N filtering for line charts to avoid cluttered charts
    if (plot.subcharts && useLineCharts) {
      const sortBy = plot.subcharts.sortByDesc ?? plot.measureId;
      const sortByData = preparedData[sortBy]?.[dimension.id] as DimensionSeries | undefined;
      dimensionSeries = filterTopCategories(dimensionSeries, sortByData, plot.subcharts.limit);
    }

    const key = `${plot.id}-${dimension.id}`;
    const title = `${plot.label} by ${dimension.label}`;

    if (plot.chart === "ewma") {
      for (const [category, series] of Object.entries(dimensionSeries)) {
        dimensionCharts.push(
          <MetricChart
            key={`${key}-${category}`}
            title={`${title} (${category})`}
            chart="ewma"
            data={series}
            measure={measure}
            ewmaOptions={plot.ewma}
          />,
        );
      }
      continue;
    }

    const chart = useLineCharts ? (
      <DimensionLineChart
        key={key}
        title={title}
        data={dimensionSeries}
        axisFormatter={axisFormatter}
        measure={measure}
        contextLabel={dimension.label}
        axisName={axisName}
      />
    ) : (
      <CumBarChart
        key={key}
        title={title}
        data={dimensionSeries}
        measure={measure}
        axisFormatter={axisFormatter}
        contextLabel={dimension.label}
        axisName={axisName}
      />
    );
    dimensionCharts.push(chart);
  }

  const tooltipContent = buildTooltipContent(measure.description, measure.owner);
  const hasSubcharts = dimensionCharts.length > 0;
  const showDimensionFilter =
    plot.chart !== "boxplot" &&
    plot.chart !== "stacked-bar" &&
    plot.chart !== "distribution-xmr" &&
    filterableDimensions.length > 0;

  const handleDimensionChange = (dimId: string) => {
    if (!dimId) {
      setDimensionFilter(null);
    } else {
      // Get first available value for this dimension (must have actual data)
      const dimData = metricData[dimId as DimensionId] as DimensionSeries | undefined;
      if (!dimData) return;
      const valuesWithData = Object.entries(dimData)
        .filter(([, series]) => series.data.some(v => v != null))
        .map(([key]) => key)
        .sort();
      const firstValue = valuesWithData[0];
      if (firstValue) {
        setDimensionFilter({ dimensionId: dimId as DimensionId, value: firstValue });
      }
    }
  };

  const handleValueChange = (value: string) => {
    if (dimensionFilter) {
      setDimensionFilter({ ...dimensionFilter, value });
    }
  };

  // Stacked-bar and distribution-xmr charts have their own title in ChartFrame, skip h3
  const showHeader = plot.chart !== "stacked-bar" && plot.chart !== "distribution-xmr";

  return (
    <div id={chartId}>
      {showHeader && (
        <h3>
          {tooltipContent ? (
            <Tooltip content={tooltipContent} asHtml align="left">
              {plot.label}
            </Tooltip>
          ) : (
            plot.label
          )}
          {showDimensionFilter && (
            <span className="dimension-filter">
              <SearchableSelect
                value={dimensionFilter?.dimensionId ?? ""}
                options={dimensionOptions}
                onChange={handleDimensionChange}
                placeholder="All"
              />
              {dimensionFilter && valueOptions.length > 0 && (
                <SearchableSelect
                  value={dimensionFilter.value}
                  options={valueOptions}
                  onChange={handleValueChange}
                  placeholder="Select value..."
                />
              )}
            </span>
          )}
          {hasSubcharts && (
            <button
              type="button"
              className="subcharts-toggle"
              onClick={() => setSubchartsExpanded(prev => !prev)}
              aria-expanded={subchartsExpanded}
            >
              {subchartsExpanded ? "Hide breakdowns" : "Show breakdowns"}
            </button>
          )}
        </h3>
      )}
      {companionSeries ? (
        <DimensionLineChart
          title={mainChartTitle}
          data={companionSeries}
          axisFormatter={axisFormatter}
          measure={measure}
          axisName={axisName}
        />
      ) : (
        <MetricChart
          title={mainChartTitle}
          chart={plot.chart}
          data={displayMainChartData ?? { timestamps: [], data: [] }}
          measure={measure}
          ratioSeries={ratioSeries}
          ewmaOptions={plot.ewma}
          stackedBarConfig={plot.stackedBar}
          distributionXmrConfig={plot.distributionXmr}
          dimensionData={displayDimensionData}
          grain={globalGrain}
        />
      )}
      {subchartsExpanded && dimensionCharts}
    </div>
  );
}

function resolveSubchartDimensions(tokens?: SubchartToken[]): DimensionConfig[] {
  if (!tokens || tokens.length === 0) return [];
  const unique = new Map<string, DimensionConfig>();

  const addDimensionById = (id?: DimensionId) => {
    if (!id || id === "all") return;
    const dimension = DIMENSION_MAP.get(id);
    if (!dimension) {
      console.warn(`No dimension config found for ${id}`);
      return;
    }
    unique.set(id, dimension);
  };

  const addDimension = (dimension?: DimensionConfig) => {
    if (!dimension) return;
    if (dimension.id === "all") return;
    unique.set(dimension.id, dimension);
  };

  const addSingles = () => {
    for (const dimension of SINGLE_DIMENSIONS) addDimensionById(dimension.id);
  };

  const addCombos = () => {
    for (const dimension of COMBO_DIMENSIONS) addDimensionById(dimension.id);
  };

  for (const token of tokens) {
    if (token === "1D") {
      addSingles();
      continue;
    }
    if (token === "2D") {
      addCombos();
      continue;
    }
    if (Array.isArray(token)) {
      if (token.length === 2) {
        addDimension(buildComboDimension(token[0], token[1]));
      } else {
        for (const dimensionId of token) addDimensionById(dimensionId as DimensionId);
      }
      continue;
    }
    addDimensionById(token as DimensionId);
  }

  return Array.from(unique.values());
}

function buildComboDimension(firstId: DimensionId, secondId: DimensionId): DimensionConfig | undefined {
  const first = DIMENSION_MAP.get(firstId);
  const second = DIMENSION_MAP.get(secondId);
  if (!first || !second) {
    console.warn(`Dimension combo references unknown ids ${firstId} & ${secondId}`);
    return undefined;
  }

  const comboId = `${firstId}_${secondId}`;
  const existing = DIMENSION_MAP.get(comboId);
  if (existing) return existing;

  return {
    id: comboId,
    label: `${first.label} & ${second.label}`,
    sql: `${first.sql} || '|' || ${second.sql}`,
    type: "string",
    dim1Field: first.dim1Field,
    dim2Field: second.dim1Field ?? second.dim2Field ?? second.id,
  } satisfies DimensionConfig;
}

function isRatioMeasure(measure?: MeasureDef) {
  if (!measure) return false;
  if (measure.unit === "ratio") return true;
  if (measure.format?.includes("%")) return true;
  return false;
}

function buildTooltipContent(description?: string, owner?: string): string | undefined {
  if (!description && !owner) return undefined;
  const segments: string[] = [];
  if (description) {
    const lines = description.split("\n");
    const htmlLines = lines
      .map(line => line.trim())
      .filter(Boolean)
      .map(line => (line.startsWith("-") ? `<li>${line.slice(1).trim()}</li>` : `<p>${line}</p>`));
    const listItems = htmlLines.filter(line => line.startsWith("<li>"));
    const paragraphs = htmlLines.filter(line => line.startsWith("<p>"));
    if (paragraphs.length > 0) segments.push(paragraphs.join(""));
    if (listItems.length > 0) segments.push(`<ul>${listItems.join("")}</ul>`);
  }
  if (owner) {
    segments.push(`<p><strong>Owner:</strong> ${owner}</p>`);
  }
  return segments.join("");
}

function getRatioSeries(plot: PlotConfig, preparedData: PreparedData, dimensionId: DimensionId) {
  if (!plot.ratioComponents) return undefined;
  const numeratorData = preparedData[plot.ratioComponents.numerator]?.[dimensionId];
  const denominatorData = preparedData[plot.ratioComponents.denominator]?.[dimensionId];
  if (!isTimeseriesChart(numeratorData) || !isTimeseriesChart(denominatorData)) return undefined;
  return { numerator: numeratorData, denominator: denominatorData };
}

function isTimeseriesChart(data: ChartData | undefined): data is Timeseries {
  if (!data) return false;
  return Array.isArray((data as Timeseries).timestamps);
}

/**
 * Filters dimension series to keep only top N categories by a metric.
 * @param dimensionSeries - The dimension series to filter
 * @param sortByData - The dimension series to use for sorting (if undefined, sorts by dimensionSeries itself)
 * @param limit - Number of top categories to keep (default: 10)
 * @returns Filtered dimension series with top N categories only
 */
function filterTopCategories(
  dimensionSeries: DimensionSeries,
  sortByData: DimensionSeries | undefined,
  limit = 10,
): DimensionSeries {
  const dataToSort = sortByData || dimensionSeries;

  return Object.fromEntries(
    Object.entries(dataToSort)
      .map(([category, series]) => ({
        category,
        total: series.data.reduce((sum, val) => sum + val, 0),
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, limit)
      .map(({ category }) => [category, dimensionSeries[category]]),
  );
}
